import type { Goal, GoalDirection, GoalMetric, GoalStatus } from '@techsofit/api-types';

/** A one-tap starting point for a goal — prefills metric, unit, direction, cadence. */
export interface GoalTemplate {
  label: string;
  title: string;
  metric: GoalMetric;
  unit: string;
  direction: GoalDirection;
  cadenceDays: number;
}

export const GOAL_TEMPLATES: GoalTemplate[] = [
  { label: 'Lose weight', title: 'Lose weight', metric: 'WEIGHT', unit: 'kg', direction: 'DOWN', cadenceDays: 14 },
  { label: 'Gain weight', title: 'Gain weight', metric: 'WEIGHT', unit: 'kg', direction: 'UP', cadenceDays: 14 },
  { label: 'Build muscle', title: 'Build muscle', metric: 'CHEST', unit: 'cm', direction: 'UP', cadenceDays: 14 },
  { label: 'Lose body fat', title: 'Lose body fat', metric: 'BODY_FAT', unit: '%', direction: 'DOWN', cadenceDays: 14 },
  { label: 'Trim waist', title: 'Trim waist', metric: 'WAIST', unit: 'cm', direction: 'DOWN', cadenceDays: 14 },
  { label: 'Run longer', title: 'Run longer', metric: 'DISTANCE', unit: 'km', direction: 'UP', cadenceDays: 7 },
  { label: 'Run faster', title: 'Run faster', metric: 'DURATION', unit: 'min', direction: 'DOWN', cadenceDays: 7 },
];

export const METRICS: { value: GoalMetric; label: string; unit: string }[] = [
  { value: 'WEIGHT', label: 'Weight', unit: 'kg' },
  { value: 'WAIST', label: 'Waist', unit: 'cm' },
  { value: 'BODY_FAT', label: 'Body fat', unit: '%' },
  { value: 'CHEST', label: 'Chest', unit: 'cm' },
  { value: 'HIPS', label: 'Hips', unit: 'cm' },
  { value: 'DISTANCE', label: 'Distance', unit: 'km' },
  { value: 'DURATION', label: 'Duration', unit: 'min' },
  { value: 'REPS', label: 'Reps', unit: 'reps' },
  { value: 'CUSTOM', label: 'Custom', unit: '' },
];

export const metricLabel = (m: GoalMetric): string => METRICS.find((x) => x.value === m)?.label ?? m;

export const GOAL_STATUS_META: Record<GoalStatus, { label: string; cls: string }> = {
  ACTIVE: { label: 'Active', cls: 'bg-ok-soft text-ok-ink' },
  PAUSED: { label: 'Paused', cls: 'bg-neutral-soft text-neutral-ink' },
  REACHED: { label: 'Reached', cls: 'bg-accent text-accent-ink' },
  ARCHIVED: { label: 'Archived', cls: 'bg-neutral-soft text-neutral-ink' },
};

/** Human cadence, e.g. "every 2 weeks", "every 7 days". */
export function cadenceLabel(days: number): string {
  if (days % 7 === 0) {
    const w = days / 7;
    return w === 1 ? 'every week' : `every ${w} weeks`;
  }
  return days === 1 ? 'every day' : `every ${days} days`;
}

/** Short value + unit, e.g. "72 kg". Trims trailing .00. */
export function valueLabel(v: number | null, unit: string): string {
  if (v == null) return '—';
  const n = Number.isInteger(v) ? String(v) : String(Number(v.toFixed(2)));
  return `${n} ${unit}`.trim();
}

/** Whether a goal still wants check-ins (drives the "due" emphasis). */
export function isTrackable(g: Goal): boolean {
  return g.status === 'ACTIVE' || g.status === 'PAUSED';
}
